// App.js

import React, { Suspense, useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useThree } from '@react-three/fiber';
import {
  OrbitControls,
  Environment,
  useEnvironment,
  Bounds,
  useBounds,
} from '@react-three/drei';
import Shoe from './Shoe';
import './style.css';

export default function App() {
  const controlsRef = useRef();
  const modelRef = useRef();
  const canvasRef = useRef();
  const [showOverlay, setShowOverlay] = useState(false);
  const interactionTimeout = useRef(null);
  const [envRotation, setEnvRotation] = useState([0, 0, 0]);
  const [loading, setLoading] = useState(true);

  // New state variable to track if the model has loaded
  const [modelLoaded, setModelLoaded] = useState(false);

  const onModelLoaded = () => {
    setModelLoaded(true);
  };

  // Handler to reset interaction timer
  const resetInteractionTimer = () => {
    if (interactionTimeout.current) {
      clearTimeout(interactionTimeout.current);
    }
    setShowOverlay(false);
    interactionTimeout.current = setTimeout(() => {
      setShowOverlay(true);
    }, 3000);
  };

  useEffect(() => {
    const canvasElement = canvasRef.current;
    canvasElement.addEventListener('mousemove', resetInteractionTimer);
    canvasElement.addEventListener('touchstart', resetInteractionTimer);
    canvasElement.addEventListener('touchmove', resetInteractionTimer);

    resetInteractionTimer();

    return () => {
      canvasElement.removeEventListener('mousemove', resetInteractionTimer);
      canvasElement.removeEventListener('touchstart', resetInteractionTimer);
      canvasElement.removeEventListener('touchmove', resetInteractionTimer);
      clearTimeout(interactionTimeout.current);
    };
  }, []);

  return (
    <div className="viewer-container" ref={canvasRef}>
      <div className="canvas-container">
        {showOverlay && (
          <img
            src="Cursor-Gif_6.gif"
            alt="Inactivity Overlay"
            className="inactivity-overlay"
          />
        )}
        <Canvas shadows camera={{ position: [0, 0, 50], fov: 20 }}>
          <Scene
            controlsRef={controlsRef}
            modelRef={modelRef}
            setEnvRotation={setEnvRotation}
            envRotation={envRotation}
            loading={loading}
            setLoading={setLoading}
            modelLoaded={modelLoaded}
            onModelLoaded={onModelLoaded}
          />
        </Canvas>
      </div>

      <a
        href="https://calendly.com/vincent-119"
        target="_blank"
        rel="noopener noreferrer"
        className="viewer-demo-button"
      >
        Book a Demo
      </a>
    </div>
  );
}

function Scene({
  controlsRef,
  modelRef,
  setEnvRotation,
  envRotation,
  loading,
  setLoading,
  modelLoaded,
  onModelLoaded,
}) {
  const envMap = useEnvironment({ files: 'blocky_photo_studio_1k.hdr' });

  useEffect(() => {
    if (modelLoaded) {
      // Delay enabling controls to ensure centering is visually complete
      setTimeout(() => {
        setLoading(false);
      }, 500); // Adjust delay as needed
    }
  }, [modelLoaded, setLoading]);

  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, -20, 5]} intensity={0.7} castShadow />
      <directionalLight position={[0, 5, 5]} intensity={0.7} castShadow />

      {/* Rotatable Environment */}
      <Environment background={false}>
        <mesh rotation={envRotation} scale={100}>
          <sphereGeometry args={[1, 32, 32]} />
          <meshBasicMaterial
            map={envMap}
            side={THREE.BackSide}
            toneMapped={false}
          />
        </mesh>
      </Environment>

      {/* Your 3D Model */}
      <Bounds fit clip observe margin={1}>
        <ModelWithBounds
          modelRef={modelRef}
          onModelLoaded={onModelLoaded}
        />
      </Bounds>

      {/* Orbit Controls */}
      <OrbitControls
        ref={controlsRef}
        autoRotate
        enablePan={false}
        enableZoom={false}
        enabled={!loading} // Disable controls while loading is true
        autoRotateSpeed={4}
        enableDamping
        dampingFactor={0.1}
        makeDefault
      />

      {/* Custom Controls */}
      <Controls setEnvRotation={setEnvRotation} controlsRef={controlsRef} />
    </>
  );
}

function ModelWithBounds({ modelRef, onModelLoaded }) {
  const bounds = useBounds();

  useEffect(() => {
    if (modelRef.current) {
      bounds.refresh(modelRef.current).fit();
    }
  }, [modelRef, bounds]);

  return (
    <Suspense fallback={null}>
      <Shoe ref={modelRef} onModelLoaded={onModelLoaded} />
    </Suspense>
  );
}

function Controls({ setEnvRotation, controlsRef }) {
  const { gl } = useThree();

  const isDragging = useRef(false);
  const prevMousePosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (event.shiftKey && event.button === 0) {
        isDragging.current = true;
        prevMousePosition.current = { x: event.clientX, y: event.clientY };
        if (controlsRef.current) controlsRef.current.enabled = false;
      }
    };

    const handlePointerMove = (event) => {
      if (isDragging.current) {
        const deltaX = event.clientX - prevMousePosition.current.x;
        const deltaY = event.clientY - prevMousePosition.current.y;

        setEnvRotation((prevRotation) => [
          prevRotation[0] + deltaY * 0.005,
          prevRotation[1] + deltaX * 0.005,
          prevRotation[2],
        ]);

        prevMousePosition.current = { x: event.clientX, y: event.clientY };
      }
    };

    const handlePointerUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        if (controlsRef.current) controlsRef.current.enabled = true;
      }
    };

    gl.domElement.addEventListener('pointerdown', handlePointerDown);
    gl.domElement.addEventListener('pointermove', handlePointerMove);
    gl.domElement.addEventListener('pointerup', handlePointerUp);

    const preventContextMenu = (e) => e.preventDefault();
    gl.domElement.addEventListener('contextmenu', preventContextMenu);

    return () => {
      gl.domElement.removeEventListener('pointerdown', handlePointerDown);
      gl.domElement.removeEventListener('pointermove', handlePointerMove);
      gl.domElement.removeEventListener('pointerup', handlePointerUp);
      gl.domElement.removeEventListener('contextmenu', preventContextMenu);
    };
  }, [gl, controlsRef, setEnvRotation]);

  return null;
}
